import { EntityList } from '@/types/Entity'
import { CountryEnum } from '@/types/enum/enumUser'

/**
 * Pays d'ouverture de chaque entite (module metier).
 *
 * REGLE PAR DEFAUT : une entite / une page N'EST OUVERTE QU'EN FRANCE. Tout autre pays
 * doit etre declare EXPLICITEMENT ici. Cette matrice est la source de verite front pour
 * masquer les blocs, sections et KPI d'un module non commercialise dans le pays actif
 * (ex: le site Belgique = School + Group uniquement : ni radio, ni PLV, ni boutique).
 */
export const ENTITY_COUNTRIES: Record<EntityList, CountryEnum[]> = {
  [EntityList.PHARMEDIGROUP]: [
    CountryEnum.FRANCE,
    CountryEnum.BELGIUM,
    CountryEnum.LUXEMBOURG,
    CountryEnum.ITALY,
  ],
  [EntityList.PHARMEDISCHOOL]: [
    CountryEnum.FRANCE,
    CountryEnum.BELGIUM,
    CountryEnum.LUXEMBOURG,
    CountryEnum.ITALY,
  ],
  [EntityList.PHARMEDISOUND]: [CountryEnum.FRANCE, CountryEnum.ITALY],
  [EntityList.PHARMEDINSIGHT]: [CountryEnum.FRANCE, CountryEnum.ITALY],
  [EntityList.PHARMEDISTORE]: [CountryEnum.FRANCE],
  [EntityList.PHARMEDISTOREBAG]: [CountryEnum.FRANCE],
  [EntityList.PHARMEDICALL]: [CountryEnum.FRANCE],
  [EntityList.PHARMEDICARE]: [CountryEnum.FRANCE],
  [EntityList.CEGIDFLEX]: [CountryEnum.FRANCE],
}

/**
 * Pays d'ouverture de la page « Gestion des points » (#791), plus etroit que son module : School
 * est aussi ouvert en Italie. Lu par l'entree de menu ET par la garde de la page, `withAuthSync`
 * ne regardant que les roles.
 */
const POINTS_MANAGEMENT_COUNTRIES = [CountryEnum.FRANCE, CountryEnum.BELGIUM, CountryEnum.LUXEMBOURG]

export const isPointsManagementOpen = (countries: CountryEnum[] | undefined | null): boolean =>
  !!countries?.some(country => POINTS_MANAGEMENT_COUNTRIES.includes(country))

/**
 * Une entite est-elle ouverte pour AU MOINS un des pays actifs ?
 * Sans pays connu on retombe sur France (comportement historique du back-office).
 */
/**
 * Fiche entreprise consideree comme FRANCAISE. Un pays non renseigne vaut France : on ne
 * masque pas de bloc sur les fiches historiques dont le pays n'a jamais ete saisi.
 */
export const isCompanyCountryFrance = (
  company: { country?: string | null } | null | undefined
): boolean => {
  const country = String(company?.country ?? '').toUpperCase()

  return !country || country === CountryEnum.FRANCE
}

export const isEntityAvailableInCountries = (
  entity: EntityList,
  countries: CountryEnum[] | undefined | null
): boolean => {
  const openCountries = ENTITY_COUNTRIES[entity] ?? [CountryEnum.FRANCE]
  const activeCountries = countries?.length ? countries : [CountryEnum.FRANCE]

  return activeCountries.some(country => openCountries.includes(country))
}

/**
 * Perimetre pays d'un profil, SANS contexte de site : `countries` (perimetre d'un admin
 * back-office) sinon le pays de la company courante (pharmacien, manager, labo). Vide =
 * inconnu, traite comme France par {@link isEntityAvailableInCountries}.
 *
 * Derivation unique, partagee par `useActiveCountries` (qui la croise en plus avec le
 * site consulte) et par le filtrage de routes.
 */
export const getProfileCountries = (dataMyProfile: any): CountryEnum[] => {
  if (dataMyProfile?.countries?.length) return dataMyProfile.countries

  const companyCountry = dataMyProfile?.selectCompanyDefault?.country

  return companyCountry ? [String(companyCountry).toUpperCase() as CountryEnum] : []
}

/**
 * Une route est-elle ouverte dans le pays actif ? Le pays d'une route est celui de SES
 * entites (`route.entity`) : une entree ne peut pas etre visible dans un pays ou son
 * module n'est pas commercialise.
 *
 * SOURCE UNIQUE : le perimetre pays d'un MODULE se declare dans ENTITY_COUNTRIES et nulle
 * part ailleurs — une route n'a plus a le repeter dans son `fnDisplay`. Celui-ci ne garde
 * que ce qu'il est seul a savoir : conditions metier (flag serveur, module deja active) et
 * restrictions PLUS FINES que le module (une page France-only dans un module multi-pays).
 */
export const isRouteAvailableInCountries = (
  route: { entity?: EntityList[] } | null | undefined,
  countries: CountryEnum[] | undefined | null
): boolean => {
  const entities = route?.entity

  if (!entities?.length) return true

  return entities.some(entity => isEntityAvailableInCountries(entity, countries))
}
